import { useCallback, useMemo, useContext } from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { TranslatorContext } from '@jutro/locale';
import {
    SgDateUtil,
    SgSubmissionUtil,
    SgIpidUtil
} from 'saga-modules';
import CommonConstants from 'sg-common-constants';
import SgIpidDetails from 'sg-ipid-details';
import SpmConstants from 'sg-spm-constants';
import stringUtil from 'gw-portals-util-js/StringUtil';
import messages from './SgSpmIpid.messages';
import IpidConfig from './SgSpmIpidConfig.json';
import metadata from './SgSpmIpid.metadata.json5';

function SgSpmIpid(props) {
    const {
        model
    } = props;

    const translator = useContext(TranslatorContext);
    const vehicle = model.lobData.privateMotor.coverables.spmVehicle;
    const vehicleCoverages = model.lobData.privateMotor.offerings[0].coverages.vehicleCoverages[0].coverages;
    const keyProtectionCover = SgSubmissionUtil.getCoverageByCode(vehicleCoverages, SpmConstants.KEY_PROTECTION_CODE);
    const legalProtectionCover = SgSubmissionUtil.getCoverageByCode(vehicleCoverages, SpmConstants.LEGAL_PROTECTION_CODE);
    const replacementCarCover = SgSubmissionUtil.getCoverageByCode(vehicleCoverages, SpmConstants.REPLACEMENT_CAR_CODE);
    const increasedPersonalAccidentCover = SgSubmissionUtil.getCoverageByCode(vehicleCoverages, SpmConstants.PERSONAL_ACCIDENT_CODE);
    const breakdownCover = SgSubmissionUtil.getCoverageByCode(vehicleCoverages, SpmConstants.BREAKDOWN_CODE);
    const isMotorHome = vehicle.vehicleType === SpmConstants.VEHICLE_TYPE_MOTORHOME;
    const { hasSelectedPNCDOnStandard } = vehicle;
    const sagaStandardCover = vehicle.coverLevel === SpmConstants.COVER_LEVEL_CODES.SAGA_STANDARD;
    const pncdCover = vehicle.isNCDProtected;
    const vehicleType = vehicle.vehicleType === SpmConstants.VEHICLE_TYPE_MOTORHOME
        ? stringUtil.capitalizeFirstLetter(SpmConstants.MOTORHOME) : stringUtil.capitalizeFirstLetter(SpmConstants.CAR);
    const { coverLevel } = vehicle;
    const isPlus = vehicle.hasFixedPrice;

    const coverLevelMessages = useMemo(() => {
        return {
            motorCoveredCountries: ['UkCovered', 'EuCovered'],
            sbaCoveredCountries: ['SBACoveredUk', 'SBACoveredIom', 'SBACoveredChannel'],
            motorObligations: ['TellUsObligations', 'ComplyObligations'],
            legalCoveredCountries: ['LegalCoveredForUninsured', 'LegalCoveredForOther'],
            legalObligations: ['LegalObligationsEnsure', 'LegalObligationsQuickly',
                'LgalObligationsComply', 'LegalObligationsIncident', 'legalObligationsClaim'],
            sbaObligations: ['SBAObligations', 'SBAObligationsQuick', 'SBAObligationsLaws']
        };
    }, []);

    const allIpidConditions = useMemo(() => {
        const ipidConditions = {};
        ipidConditions.hasLegal = legalProtectionCover.selected;
        ipidConditions.hasKeyProtection = keyProtectionCover.selected;
        ipidConditions.hasReplacementCar = replacementCarCover.selected;
        ipidConditions.hasPersonalAccident = increasedPersonalAccidentCover.selected;
        ipidConditions.hasSBA = breakdownCover.selected;
        ipidConditions.hasPNCD = pncdCover;
        ipidConditions.isSagaStandard = sagaStandardCover;
        ipidConditions.isMotorHome = isMotorHome;
        ipidConditions.isNotMotorHome = !isMotorHome;
        ipidConditions.isFixedPrice = vehicle.hasFixedPrice;
        ipidConditions.hasKeyCoverAndNotFixedPrice = keyProtectionCover.selected && !vehicle.hasFixedPrice;
        ipidConditions.hasPNCDAndNotStandard = pncdCover && !sagaStandardCover;
        ipidConditions.hasPNCDSelected = pncdCover && hasSelectedPNCDOnStandard;
        ipidConditions.isNotSagaStandard = !sagaStandardCover;
        ipidConditions.isBusinessUse = vehicle.vehicleUse === SpmConstants.VEHICLE_USE.BUSINESS;
        ipidConditions.isSdpCommuting = vehicle.vehicleUse === SpmConstants.VEHICLE_USE.SDP_COMMUTING;
        ipidConditions.isPersonalBusiness = vehicle.vehicleUse === SpmConstants.VEHICLE_USE.PERSONAL_BUSINESS;
        ipidConditions.isNotFixedPrice = !vehicle.hasFixedPrice;
        ipidConditions.homeCallCover = breakdownCover && breakdownCover.terms[0].chosenTerm === SpmConstants.SBA_TERMS.HOME_CALL;
        ipidConditions.essentialCover = breakdownCover && breakdownCover.terms[0].chosenTerm === SpmConstants.SBA_TERMS.ESSENTIAL;
        ipidConditions.comprehensiveCover = breakdownCover && breakdownCover.terms[0].chosenTerm === SpmConstants.SBA_TERMS.COMPREHENSIVE;
        ipidConditions.nationalRecoveryCover = breakdownCover && breakdownCover.terms[0].chosenTerm === SpmConstants.SBA_TERMS.NATIONAL_RECOVERY;
        ipidConditions.premierCover = breakdownCover && breakdownCover.terms[0].chosenTerm === SpmConstants.SBA_TERMS.PREMIER;
        ipidConditions.assistance = breakdownCover && breakdownCover.terms[0].chosenTerm === SpmConstants.SBA_TERMS.ASSISTANCE;
        ipidConditions.assistancePlus = breakdownCover && breakdownCover.terms[0].chosenTerm === SpmConstants.SBA_TERMS.ASSISTANCE_PLUS;
        return ipidConditions;
    }, [breakdownCover, hasSelectedPNCDOnStandard, increasedPersonalAccidentCover.selected,
        isMotorHome, keyProtectionCover.selected, legalProtectionCover.selected, pncdCover,
        replacementCarCover.selected, sagaStandardCover, vehicle.hasFixedPrice, vehicle.vehicleUse]);

    // Build all meessage params to pass to messages
    const messageParams = useMemo(() => {
        return {
            vehicle: vehicleType
        };
    }, [vehicleType]);

    // To check which items to return based on conditions
    const conditionsApply = (covers, item) => {
        const conditions = [];
        if (item.productType) {
            conditions.push(covers[item.productType]);
        }
        if (item.coverLevel) {
            conditions.push(covers[item.coverLevel]);
        }
        if (item.condition) {
            conditions.push(covers[item.condition]);
        }
        if (conditions.length === 0) {
            return true;
        } if (conditions.length === 1) {
            return conditions[0];
        } if (conditions.length === 2) {
            return conditions[0] && conditions[1];
        }
        return conditions[0] && conditions[1] && conditions[2];
    };

    // Pull messages and title that matches the condition based on coverlevel and translate them
    const getMessages = useCallback((cover, product, type) => {
        const productConfig = IpidConfig[product];
        return SgIpidUtil.getMessages(cover, product, type, productConfig, conditionsApply, messages, messageParams, translator);
    }, [translator, messageParams]);

    const coverName = useMemo(() => {
        let coverLevelName;
        if (coverLevel === SpmConstants.COVER_LEVEL_CODES.SAGA_STANDARD) {
            coverLevelName = SpmConstants.SG_COVER_LEVELS.MOTOR_STANDARD;
        }
        if (coverLevel === SpmConstants.COVER_LEVEL_CODES.SAGA_SELECT) {
            coverLevelName = SpmConstants.SG_COVER_LEVELS.MOTOR_COMPREHENSIVE;
        }
        if (coverLevel === SpmConstants.COVER_LEVEL_CODES.SAGA_PLUS) {
            coverLevelName = SpmConstants.SG_COVER_LEVELS.MOTOR_FIXED_PRICE;
        }
        return coverLevelName;
    }, [coverLevel]);

    const contactPhoneNumber = useMemo(() => {
        return isMotorHome ? CommonConstants.SAGA_PHONE_NUMBERS.SAGA_IPID_MOTORHOME_PHONE_NUMBER
            : CommonConstants.SAGA_PHONE_NUMBERS.SAGA_IPID_OTHER_PHONE_NUMBER;
    }, [isMotorHome]);

    const initProductTitle = useCallback((key) => {
        let productKey = `${SpmConstants.IPID_PAGE.ProductTitle}`;
        if (key.toLowerCase() === SpmConstants.MOTOR) {
            productKey += `${coverName}`;
        }
        if (key === SpmConstants.BREAKDOWN_CODE && breakdownCover.terms[0].chosenTerm) {
            const breakdownCoverName = stringUtil.capitalizeFirstLetter(breakdownCover.terms[0].chosenTerm.replaceAll('_', ''));
            productKey += `${breakdownCoverName}`;
        }
        return productKey;
    }, [breakdownCover.terms, coverName]);

    // Build InsuranceType description based on coverlevels and productType
    const getInsuranceTypeDesc = useCallback((key) => {
        translator(messages[`${key}${SpmConstants.IPID_PAGE.InsuranceTypeDesc}`]);
        return translator(messages[`${key}${SpmConstants.IPID_PAGE.InsuranceTypeDesc}`]);
    }, [translator]);

    const createListFromMessages = useCallback((messageArray, params) => {
        return SgIpidUtil.createListFromMessages(messageArray, params, messages, translator);
    }, [translator]);

    const initCommonData = useCallback((key, keyTitle) => {
        const productTypeTitle = keyTitle ? `${SpmConstants.IPID_PAGE.Title}${keyTitle}` : `${SpmConstants.IPID_PAGE.Title}${key}`;
        const coverLevelTitle = `${key}${initProductTitle(key)}`;
        return {
            insuredList: getMessages(allIpidConditions, key, `${CommonConstants.IPID_PAGE.Insured}`),
            notInsuredList: getMessages(allIpidConditions, key, `${CommonConstants.IPID_PAGE.NotInsured}`),
            restrictionsList: getMessages(allIpidConditions, key, `${CommonConstants.IPID_PAGE.Restrictions}`),
            coveredList: [],
            obligationsList: [],
            header: {
                title: translator(messages[productTypeTitle], { vehicleType: vehicleType }),
                suffix: key,
                product: translator(messages[coverLevelTitle], { vehicleType: vehicleType }),
                insuranceTypeDesc: getInsuranceTypeDesc(key)
            },
            date: {
                periodStartDate: SgDateUtil.toLongDate(model.baseData.periodStartDate),
                periodEndDate: SgDateUtil.addDaysToLongDate(-1, model.baseData.periodEndDate)
            },
            phoneNumber: contactPhoneNumber
        };
    }, [allIpidConditions, contactPhoneNumber, getInsuranceTypeDesc, getMessages, initProductTitle,
        model.baseData.periodEndDate, model.baseData.periodStartDate, translator, vehicleType]);

    const initIpidData = useMemo(() => {
        const data = initCommonData(stringUtil.capitalizeFirstLetter(SpmConstants.MOTOR), vehicleType);
        data.coveredList = createListFromMessages(coverLevelMessages.motorCoveredCountries, null);
        data.obligationsList = createListFromMessages(coverLevelMessages.motorObligations, null);
        data.howDoIPay = translator(messages.HowDoIPay);
        data.endDateMessage = '';
        return data;
    }, [coverLevelMessages.motorCoveredCountries, coverLevelMessages.motorObligations,
        createListFromMessages, initCommonData, translator, vehicleType]);

    const initIpidAddonData = useCallback((key) => {
        const data = initCommonData(key, null);
        data.howDoIPay = translator(messages.HowDoIPayOther, { vehicle: vehicleType });
        data.endDateMessage = translator(messages.EndDateMessage, { vehicle: vehicleType });
        data.hideCloseButton = true;
        return data;
    }, [coverLevelMessages.commonCOveredCountries, createListFromMessages, initCommonData, translator, vehicleType]);

    const initIpidLegalData = useMemo(() => {
        const data = initIpidAddonData(SpmConstants.LEGAL_PROTECTION_CODE);
        data.coveredList = createListFromMessages(coverLevelMessages.legalCoveredCountries, null);
        data.obligationsList = createListFromMessages(coverLevelMessages.legalObligations, null);
        return data;
    }, [coverLevelMessages.legalObligations, coverLevelMessages.legalCoveredCountries, createListFromMessages, initIpidAddonData]);

    const initIpidSBAData = useMemo(() => {
        const data = initIpidAddonData(SpmConstants.BREAKDOWN_CODE);
        data.obligationsList = createListFromMessages(coverLevelMessages.sbaObligations, null);
        data.header.title = isMotorHome ? translator(messages.TitleMotorHomeSBA) : translator(messages.TitleMotorSBA);
        data.header.insuranceTypeDesc = isMotorHome
            ? translator(messages.SBAMotorHomeInsuranceTypeDesc) : translator(messages.SBAMotorInsuranceTypeDesc);
        data.coveredList = createListFromMessages(coverLevelMessages.sbaCoveredCountries, null);
        if (allIpidConditions.assistancePlus || allIpidConditions.premierCover) {
            data.coveredList.push(translator(messages.SBACoveredPrem));
        }
        return data;
    }, [allIpidConditions.assistancePlus, allIpidConditions.premierCover, coverLevelMessages.sbaObligations, coverLevelMessages.sbaCoveredCountries,
        createListFromMessages, initIpidAddonData, isMotorHome, translator]);

    const overrides = {
        motorIpid: {
            data: initIpidData
        },
        motorIpidLegal: {
            visible: legalProtectionCover.selected && !isPlus,
            data: initIpidLegalData
        },
        motorIpidBreakdown: {
            visible: breakdownCover.selected,
            data: initIpidSBAData
        }
    };

    const resolvers = {
        resolveComponentMap: {
            SgIpidDetails: SgIpidDetails
        },
    };

    return renderContentFromMetadata(metadata.componentContent, overrides, resolvers);
}

export default SgSpmIpid;
